import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PAGE_LIMIT_MAX, pendingSignatures, errorWords } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTableColumns } from "@/components/app/patterns/data-table-columns";
import { ListToolbar } from "@/components/app/patterns/list-toolbar";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { SendDocumentsEmailDialog } from "@/components/app/sending/send-documents-email-dialog";
import { SendFaxDialog } from "@/components/app/sending/send-fax-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { saveFile, showFile } from "@/lib/files/download";
import { useFacility } from "@/lib/facility/facility";
import { AlertTriangle } from "@/lib/icons";
import { orpc, orpcClient } from "@/lib/orpc";
import { filterPatch, readFilters, stringParams, KEEP_PLACE, optionalSearch } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { IncidentBatchBar, type BatchMode } from "./-components/incident-batch-bar";
import { IncidentDialog, type IncidentRow } from "./-components/incident-dialog";
import {
  IncidentFilters,
  NO_INCIDENT_FILTERS,
  panelCount,
  type IncidentFilterState,
} from "./-components/incident-filters";
import { IncidentStatusChips } from "./-components/incident-status-chips";
import { edState, IncidentTable } from "./-components/incident-table";
import { useAllIncidents } from "./-components/all-incidents";
import { MergeIncidentsDialog } from "./-components/merge-incidents-dialog";
import { SignDialog } from "./-components/sign-dialog";
import { SearchField } from "@/components/app/patterns/search-field";

const FILTER_KEYS = ["resident", "severity", "status", "ed", "preset", "from", "to"] as const;
type IncidentSearch = { q?: string } & Partial<IncidentFilterState>;

export const Route = createFileRoute("/_authenticated/_care/incidents/")({
  validateSearch: (search: Record<string, unknown>): IncidentSearch => ({
    q: optionalSearch(search.q),
    ...stringParams(FILTER_KEYS, search),
  }),
  component: IncidentsPage,
});

const fail = (error: Error) => toast.error(errorWords(error));

function IncidentsPage() {
  const { facilityId } = useFacility();
  const canManage = useCan("incident:manage");
  const queryClient = useQueryClient();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<IncidentSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const { text: query, change: setQuery } = useSearchParamText(params.q, (q) => patch({ q }));
  const filters = readFilters(NO_INCIDENT_FILTERS, params);
  const setFilters = (next: IncidentFilterState) => patch(filterPatch(NO_INCIDENT_FILTERS, next));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<BatchMode | null>(null);
  const [sending, setSending] = useState<{ mode: "fax" | "email"; files: File[] } | null>(null);
  const [dialog, setDialog] = useState<{ incident: IncidentRow | null } | null>(null);
  const [removing, setRemoving] = useState<IncidentRow | null>(null);
  const [signing, setSigning] = useState(false);
  const [merging, setMerging] = useState(false);

  const list = useAllIncidents({
    facilityId,
    ...(filters.from ? { from: new Date(`${filters.from}T00:00:00`) } : {}),
    ...(filters.to ? { to: new Date(`${filters.to}T23:59:59.999`) } : {}),
  });
  const residents = useQuery(
    orpc.care.residents.list.queryOptions({
      input: { facilityId, status: "active", limit: PAGE_LIMIT_MAX, offset: 0 },
    }),
  );
  const queue = useQuery(orpc.care.incidents.signatures.queue.queryOptions({ input: {} }));
  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.care.incidents.key() });
  const print = useMutation(
    orpc.care.incidents.exportPdf.mutationOptions({ onSuccess: showFile, onError: fail }),
  );
  const remove = useMutation(
    orpc.care.incidents.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Incident deleted");
        await invalidate();
      },
      onError: fail,
    }),
  );
  const sign = useMutation(
    orpc.care.incidents.signatures.sign.mutationOptions({
      onSuccess: async (result) => {
        setSigning(false);
        if (result.failed)
          toast.error(
            `${result.succeeded} signed, ${result.failed} could not be: ${result.results.find((row) => row.error)?.error ?? ""}`,
          );
        else
          toast.success(
            `${result.succeeded} ${result.succeeded === 1 ? "incident" : "incidents"} signed`,
          );
        await invalidate();
      },
      onError: fail,
    }),
  );

  const all = list.data?.rows ?? [];
  const people = residents.data?.rows ?? [];
  const needle = query.trim().toLowerCase();
  /* Everything but the status, so each status chip can count what choosing it would show. */
  const narrowed = all.filter(
    (row) =>
      (!needle ||
        `${row.residentFirstName} ${row.residentLastName}`.toLowerCase().includes(needle) ||
        row.description.toLowerCase().includes(needle)) &&
      (filters.resident === "all" || row.residentId === filters.resident) &&
      (filters.severity === "all" || row.severity === filters.severity) &&
      (filters.ed === "all" || edState(row) === filters.ed),
  );
  const incidents = narrowed.filter(
    (row) => filters.status === "all" || row.status === filters.status,
  );
  const rooms = new Map(people.map((person) => [person.id, person]));
  const hasFilters = Boolean(needle) || panelCount(filters) > 0;
  const openCount = all.filter((row) => row.status !== "resolved").length;
  const selectedIds = incidents.filter((row) => selected.has(row.id)).map((row) => row.id);
  const pendingItems = (queue.data?.items ?? []).flatMap((incident) =>
    pendingSignatures(incident).map((phase) => ({ incidentId: incident.id, phase })),
  );
  const clear = () => {
    void navigate({ search: {}, ...KEEP_PLACE });
  };

  const runBatch = async (mode: BatchMode) => {
    if (selectedIds.length === 0) return;
    setBusy(mode);
    try {
      const file = await orpcClient.care.incidents.exportBatchPdf({ incidentIds: selectedIds });
      if (mode === "print") showFile(file);
      else if (mode === "export") saveFile(file);
      else {
        const name =
          selectedIds.length === 1
            ? "incident-report.pdf"
            : `incident-reports-${selectedIds.length}.pdf`;
        setSending({ mode, files: [new File([file], name, { type: "application/pdf" })] });
      }
    } catch (error) {
      fail(error as Error);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Incidents"
        count={list.isSuccess ? all.length : undefined}
        meta={
          list.isPending
            ? "Loading…"
            : list.isError
              ? undefined
              : `${openCount} still open${hasFilters ? `, ${incidents.length} matching` : ""}`
        }
        actions={
          <>
            {queue.data?.eligible && pendingItems.length ? (
              <Button variant="outline" onClick={() => setSigning(true)}>
                Sign {pendingItems.length} waiting
              </Button>
            ) : null}
            {canManage ? (
              <Button onClick={() => setDialog({ incident: null })}>
                <AlertTriangle className="size-4" />
                Report incident
              </Button>
            ) : null}
          </>
        }
      />

      {list.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load the incidents."
            error={list.error}
            onRetry={() => void list.refetch()}
            retrying={list.isFetching}
          />
        </Card>
      ) : (
        <IncidentTable
          rows={incidents}
          residents={rooms}
          selected={selected}
          onSelected={setSelected}
          canManage={canManage}
          printing={print.isPending}
          pending={list.isPending}
          onPrint={(incident) => print.mutate({ incidentId: incident.id })}
          onEdit={(incident) => setDialog({ incident })}
          onDelete={setRemoving}
          renderToolbar={(table) => (
            <>
              {/* The page searches and filters, so each status chip can count what it would
                  show. */}
              <ListToolbar
                search={
                  <SearchField
                    className="sm:w-full"
                    placeholder="Search incidents"
                    value={query}
                    onChange={setQuery}
                  />
                }
                chips={
                  list.isSuccess && all.length > 0 ? (
                    <IncidentStatusChips
                      rows={narrowed}
                      value={filters.status}
                      onChange={(status) => setFilters({ ...filters, status })}
                    />
                  ) : null
                }
                trailing={
                  <>
                    <IncidentFilters filters={filters} residents={people} onChange={setFilters} />
                    {hasFilters ? (
                      <Button variant="ghost" onClick={clear}>
                        Clear filters
                      </Button>
                    ) : null}
                    <DataTableColumns table={table} />
                  </>
                }
              />
              {selectedIds.length > 0 ? (
                <IncidentBatchBar
                  count={selectedIds.length}
                  busy={busy}
                  onRun={(mode) => void runBatch(mode)}
                  onMerge={canManage && selectedIds.length > 1 ? () => setMerging(true) : undefined}
                  onClear={() => setSelected(new Set())}
                />
              ) : null}
            </>
          )}
          empty={
            <EmptyState
              title={hasFilters ? "No incidents match these filters" : "No incidents reported"}
              description={
                hasFilters
                  ? "Widen the search or clear a filter to see more."
                  : "Falls, medication errors and injuries appear here once someone reports them."
              }
              action={
                hasFilters ? (
                  <Button variant="outline" onClick={clear}>
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          }
        />
      )}

      {dialog ? (
        <IncidentDialog
          incident={dialog.incident}
          residents={people}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {merging ? (
        <MergeIncidentsDialog
          reports={incidents.filter((row) => selected.has(row.id))}
          onClose={() => setMerging(false)}
          onMerged={() => setSelected(new Set())}
        />
      ) : null}
      {removing ? (
        <RemoveConfirm
          title={`Delete the incident for ${removing.residentFirstName} ${removing.residentLastName}?`}
          description="Deleting this report removes it and its actions taken permanently. Use it for a report opened on the wrong resident or started twice."
          confirmLabel="Delete incident"
          onConfirm={() => remove.mutate({ incidentId: removing.id })}
          onClose={() => setRemoving(null)}
        />
      ) : null}
      <SendFaxDialog
        open={sending?.mode === "fax"}
        onOpenChange={(open) => (open ? null : setSending(null))}
        facilityId={facilityId}
        attachments={sending?.files}
      />
      <SendDocumentsEmailDialog
        open={sending?.mode === "email"}
        onOpenChange={(open) => (open ? null : setSending(null))}
        facilityId={facilityId}
        attachments={sending?.files}
      />
      <SignDialog
        open={signing}
        count={pendingItems.length}
        pending={sign.isPending}
        onOpenChange={setSigning}
        onSign={(password) => sign.mutate({ password, items: pendingItems })}
      />
    </div>
  );
}
